import dayjs from "dayjs";

import { Card } from "@components/cards/card";
import { Pagination } from "@components/pagination";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { Masks } from "@hooks/functions/Masks";
import { abbreviateName, formatName } from "@hooks/functions/formatName";
import {
  CustomerTypeEnum,
  CustomerTypeTypes,
} from "@hooks/dtos/user-dto";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaBroom,
  FaCalendarDays,
  FaChartLine,
  FaCircleCheck,
  FaCircleXmark,
  FaFingerprint,
} from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

interface IUserAccessChartDTO {
  date: string;
  uniqueAccesses: number;
  successfulAccesses: number;
  failedAccesses: number;
  totalAccesses: number;
}

interface IUserAccessLogDTO {
  id: number;
  customerId: number;
  type: CustomerTypeTypes;
  name: string;
  document: string;
  success: "y" | "n";
  createdAt: string;
}

interface IUserAccessMappingResponseDTO {
  summary: {
    uniqueAccesses: number;
    successfulAccesses: number;
    totalAccesses: number;
    failedAccesses: number;
    monitoredDays: number;
  };
  chart: IUserAccessChartDTO[];
  logs: {
    total: number;
    totalPages: number;
    list: IUserAccessLogDTO[];
  };
}

type IUserAccessChartApiDTO = Partial<IUserAccessChartDTO> & {
  failures?: number;
  total?: number;
};

type IUserAccessMappingApiResponseDTO = Partial<
  Omit<IUserAccessMappingResponseDTO, "chart" | "logs">
> & {
  summary?: IUserAccessSummaryApiDTO;
  chart?: IUserAccessChartApiDTO[];
  logs?: Partial<IUserAccessMappingResponseDTO["logs"]>;
};

type IUserAccessSummaryApiDTO = Partial<
  IUserAccessMappingResponseDTO["summary"]
> & {
  failures?: number;
};

const masks = new Masks();
const takeOptions = [10, 30, 50, 100];
const defaultStartDate = dayjs().subtract(7, "day").format("YYYY-MM-DD");
const defaultEndDate = dayjs().format("YYYY-MM-DD");

const customerTypeSelect: { type: CustomerTypeTypes; text: string }[] = [
  { type: CustomerTypeEnum.CUSTOMER, text: "Cliente" },
  { type: CustomerTypeEnum.SALESPERSON, text: "Consultor" },
  { type: CustomerTypeEnum.MANAGER, text: "Gerente" },
];

const initialAccessMapping: IUserAccessMappingResponseDTO = {
  summary: {
    uniqueAccesses: 0,
    successfulAccesses: 0,
    totalAccesses: 0,
    failedAccesses: 0,
    monitoredDays: 0,
  },
  chart: [],
  logs: {
    total: 0,
    totalPages: 1,
    list: [],
  },
};

const toNumber = (value: unknown) => Number(value || 0);

const normalizeAccessMapping = (
  response: IUserAccessMappingApiResponseDTO,
): IUserAccessMappingResponseDTO => {
  const summary: IUserAccessSummaryApiDTO = response.summary || {};
  const chart = (response.chart || []).map((item) => {
    const uniqueAccesses = toNumber(item.uniqueAccesses);
    const failedAccesses = toNumber(item.failedAccesses ?? item.failures);
    const successfulAccesses = toNumber(
      item.successfulAccesses ?? item.uniqueAccesses,
    );
    const totalAccesses = toNumber(
      item.totalAccesses ?? item.total ?? successfulAccesses + failedAccesses,
    );

    return {
      date: item.date || "",
      uniqueAccesses,
      successfulAccesses,
      failedAccesses,
      totalAccesses,
    };
  });

  const failedAccesses = toNumber(summary.failedAccesses ?? summary.failures);
  const successfulAccesses = toNumber(
    summary.successfulAccesses ?? summary.uniqueAccesses,
  );

  return {
    summary: {
      uniqueAccesses: toNumber(summary.uniqueAccesses),
      successfulAccesses,
      failedAccesses,
      totalAccesses: toNumber(
        summary.totalAccesses ?? successfulAccesses + failedAccesses,
      ),
      monitoredDays: toNumber(summary.monitoredDays),
    },
    chart,
    logs: {
      total: toNumber(response.logs?.total),
      totalPages: toNumber(response.logs?.totalPages) || 1,
      list: response.logs?.list || [],
    },
  };
};

const formatDocument = (document: string) => {
  const cleanDocument = document.replace(/\D/g, "");
  if (cleanDocument.length === 11) return masks.cpf(cleanDocument);
  if (cleanDocument.length === 14) return masks.cnpj(cleanDocument);
  return document || "~~";
};

export const UserAccessMapping = () => {
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [logAmount, setLogAmount] = useState(0);

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [type, setType] = useState<CustomerTypeTypes | undefined>();
  const [success, setSuccess] = useState<"y" | "n" | undefined>();
  const [uniqueOnly, setUniqueOnly] = useState<"y" | "n" | undefined>();
  const [resultTime, setResultTime] = useState("Período Selecionado");

  const [newSearch, setNewSearch] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] =
    useState<IUserAccessMappingResponseDTO>(initialAccessMapping);

  const chartMax = useMemo(() => {
    return Math.max(...data.chart.map((item) => item.totalAccesses), 1);
  }, [data.chart]);

  const chartPoints = useMemo(() => {
    const width = 860;
    const height = 220;
    const step = data.chart.length > 1 ? width / (data.chart.length - 1) : 0;

    return data.chart.map((item, index) => {
      const x = index * step + 20;
      const y = height - (item.totalAccesses / chartMax) * 190 + 10;

      return { x, y, item };
    });
  }, [chartMax, data.chart]);

  const linePath = useMemo(() => {
    return chartPoints
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" ");
  }, [chartPoints]);

  const bestDay = useMemo(() => {
    if (!data.chart.length) return undefined;

    return data.chart.reduce(
      (best, current) =>
        current.totalAccesses > best.totalAccesses ? current : best,
      data.chart[0],
    );
  }, [data.chart]);

  const successRate = useMemo(() => {
    if (!data.summary.totalAccesses) return 0;
    return (
      (data.summary.successfulAccesses / data.summary.totalAccesses) *
      100
    );
  }, [data.summary.successfulAccesses, data.summary.totalAccesses]);

  const buildFilterParams = useCallback(() => {
    return Object.fromEntries(
      Object.entries({
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(type && { type }),
        ...(success && { success }),
        ...(uniqueOnly && { uniqueOnly }),
      }),
    );
  }, [endDate, startDate, success, type, uniqueOnly]);

  const getAccessMapping = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          page: (page || 1).toString(),
          take: take.toString(),
          ...buildFilterParams(),
        }),
      ).toString()}`;

      const result = await api.get<IUserAccessMappingApiResponseDTO>(
        `/customer/management/users/access-map${params}`,
      );
      const normalizedData = normalizeAccessMapping(result.data);

      setData(normalizedData);
      setLogAmount(normalizedData.logs.total);
      setTotalPages(normalizedData.logs.totalPages || 1);
      setResultTime(
        startDate.length + endDate.length > 0
          ? "Período Selecionado"
          : "Todo Período",
      );
      setNewSearch(false);
    } catch (error: any) {
      console.error(error);
      pushToastify({
        message:
          error?.message || "Houve um erro ao consultar o mapeamento de acessos!",
        type: "error",
        theme: "dark",
        pauseOnHover: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    buildFilterParams,
    endDate.length,
    page,
    pushToastify,
    startDate.length,
    take,
  ]);

  const cleanFilters = () => {
    setStartDate("");
    setEndDate("");
    setType(undefined);
    setSuccess(undefined);
    setUniqueOnly(undefined);
    setPage(1);
    setTotalPages(1);
    setNewSearch(true);
  };

  useEffect(() => {
    getAccessMapping();
  }, [getAccessMapping]);

  useEffect(() => {
    if (newSearch) getAccessMapping();
  }, [getAccessMapping, newSearch]);

  return (
    <div
      className="container-fluid d-flex flex-column justify-content-start align-items-center"
      style={{
        paddingBottom: "2.5rem",
        paddingTop: "clamp(1rem, 2vw, 2rem)",
        paddingLeft: "clamp(1rem, 5vw, 10rem)",
        paddingRight: "clamp(1rem, 5vw, 10rem)",
      }}
    >
      <div className="col-12">
        <Card className="shadow-sm rounded mb-4 border-0">
          <div className="card-body p-4">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="rounded-3 d-inline-flex align-items-center justify-content-center p-3 bg-warning-subtle text-warning">
                  <FaFingerprint size={30} />
                </div>

                <div>
                  <span className="badge rounded-pill text-bg-warning mb-2">
                    Auditoria
                  </span>
                  <h3 className="fw-bold mb-1">Mapeamento de Acessos</h3>
                  <p className="text-muted mb-0">
                    Monitore logins por data, falhas de acesso e volume total
                    de autenticações.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() => navigate("/management/users")}
              >
                <FaArrowLeft />
                Voltar
              </button>
            </div>
          </div>
        </Card>

        <div className="row">
          <div className="col-md-12">
            <Card>
              <div className="card-header">
                <div className="card-title">Filtrar Registros</div>
                <div className="card-category">
                  Consulte acessos por período e resultado do login.
                </div>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12 col-sm-6 col-xl">
                    <div className="form-group mb-0">
                      <label htmlFor="startDate">Data Inicial</label>
                      <input
                        type="date"
                        className="form-control"
                        onChange={(event) => setStartDate(event.target.value)}
                        id="startDate"
                        value={startDate}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-xl">
                    <div className="form-group mb-0">
                      <label htmlFor="endDate">Data Final</label>
                      <input
                        type="date"
                        className="form-control"
                        onChange={(event) => setEndDate(event.target.value)}
                        id="endDate"
                        value={endDate}
                      />
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-xl">
                    <div className="form-group mb-0">
                      <label htmlFor="type">Tipo de Usuário</label>
                      <select
                        id="type"
                        className="form-control"
                        value={type || ""}
                        onChange={(event) => {
                          const value = event.target.value;
                          setType(
                            value
                              ? (value as CustomerTypeTypes)
                              : undefined,
                          );
                        }}
                      >
                        <option value="">Todos</option>
                        {customerTypeSelect.map((item) => (
                          <option key={item.type} value={item.type}>
                            {item.text}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-xl">
                    <div className="form-group mb-0">
                      <label htmlFor="uniqueOnly">Acessos Únicos</label>
                      <select
                        id="uniqueOnly"
                        className="form-control"
                        value={uniqueOnly || ""}
                        onChange={(event) => {
                          const value = event.target.value;
                          setUniqueOnly(value ? (value as "y" | "n") : undefined);
                        }}
                      >
                        <option value="">Todos</option>
                        <option value="y">Somente únicos</option>
                        <option value="n">Com duplicados</option>
                      </select>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-xl">
                    <div className="form-group mb-0">
                      <label htmlFor="success">Resultado</label>
                      <select
                        id="success"
                        className="form-control"
                        value={success || ""}
                        onChange={(event) => {
                          const value = event.target.value;
                          setSuccess(value ? (value as "y" | "n") : undefined);
                        }}
                      >
                        <option value="">Todos</option>
                        <option value="y">Sucesso</option>
                        <option value="n">Falha</option>
                      </select>
                    </div>
                  </div>

                </div>

                <div className="row mt-3">
                  <div className="col-12 d-flex justify-content-end gap-2">
                    <button
                      className="btn btn-sm btn-success px-5"
                      onClick={() => getAccessMapping()}
                    >
                      <span className="text-white">Filtrar</span>
                    </button>
                    <button
                      className="btn btn-sm btn-warning"
                      onClick={cleanFilters}
                      title="Limpar todos os filtros."
                    >
                      <FaBroom className="text-white" />
                    </button>
                  </div>
                </div>
              </div>
              <span className="position-absolute end-0 bg-info text-white p-2 rounded-end rounded-bottom-0">
                {resultTime}
              </span>
            </Card>
          </div>
        </div>

        <div className="row g-4 mb-4">
          {[
            {
              title: "Acessos Únicos",
              value: data.summary.uniqueAccesses,
              icon: FaFingerprint,
              className: "bg-info-subtle text-info",
            },
            {
              title: "Sucesso",
              value: data.summary.successfulAccesses,
              icon: FaCircleCheck,
              className: "bg-success-subtle text-success",
            },
            {
              title: "Falhas",
              value: data.summary.failedAccesses,
              icon: FaCircleXmark,
              className: "bg-danger-subtle text-danger",
            },
            {
              title: "Total Geral",
              value: data.summary.totalAccesses,
              icon: FaChartLine,
              className: "bg-primary-subtle text-primary",
            },
            {
              title: "Dias Relevantes",
              value: data.summary.monitoredDays,
              icon: FaCalendarDays,
              className: "bg-warning-subtle text-warning",
            },
          ].map((item) => {
            const Icon = item.icon;

            return (
              <div key={item.title} className="col-12 col-md-6 col-xl">
                <Card className="h-100 shadow-sm border-0">
                  <div className="card-body p-4">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <p className="text-muted mb-2">{item.title}</p>
                        <h2 className="fw-bold mb-0">
                          {isLoading
                            ? "--"
                            : (item.value || 0).toLocaleString("pt-BR")}
                        </h2>
                      </div>
                      <div
                        className={`rounded-3 d-inline-flex align-items-center justify-content-center p-3 ${item.className}`}
                      >
                        <Icon size={22} />
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>

        <div className="row g-4 mb-4">
          <div className="col-12 col-xl-9">
            <Card className="h-100 shadow-sm border-0">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="card-title mb-1">Acessos por Data</h4>
                    <p className="card-category mb-0">
                      Sucessos, falhas, total geral e acessos únicos por dia.
                    </p>
                  </div>
                  <FaChartLine className="text-success fs-4" />
                </div>
              </div>
              <div className="card-body p-4">
                <div className="d-flex flex-wrap gap-3 mb-4">
                  <span className="d-flex align-items-center gap-2">
                    <span className="rounded d-inline-block bg-success" style={{ width: 12, height: 12 }} />
                    Sucesso
                  </span>
                  <span className="d-flex align-items-center gap-2">
                    <span className="rounded d-inline-block bg-danger" style={{ width: 12, height: 12 }} />
                    Falhas
                  </span>
                  <span className="d-flex align-items-center gap-2">
                    <span className="rounded d-inline-block bg-primary" style={{ width: 12, height: 12 }} />
                    Total Geral
                  </span>
                </div>

                <div className="w-100" style={{ overflowX: "auto" }}>
                  <svg viewBox="0 0 900 285" style={{ minWidth: 760, width: "100%" }}>
                    {[0, 1, 2, 3, 4].map((line) => {
                      const y = 230 - line * 48;
                      const value = Math.round((chartMax / 4) * line);

                      return (
                        <g key={line}>
                          <line
                            x1="20"
                            y1={y}
                            x2="880"
                            y2={y}
                            stroke="#eef1f5"
                          />
                          <text x="0" y={y + 4} fontSize="11" fill="#8a94a6">
                            {value}
                          </text>
                        </g>
                      );
                    })}

                    {chartPoints.map((point) => {
                      const barHeight =
                        (point.item.successfulAccesses / chartMax) * 190;
                      const failHeight = (point.item.failedAccesses / chartMax) * 190;

                      return (
                        <g key={point.item.date} style={{ cursor: "default" }}>
                          <title>
                            {`${dayjs(point.item.date).format(
                              "DD/MM/YYYY",
                            )} | Acessos Únicos: ${
                              point.item.uniqueAccesses
                            } | Sucesso: ${
                              point.item.successfulAccesses
                            } | Falhas: ${point.item.failedAccesses} | Total Geral: ${
                              point.item.totalAccesses
                            }`}
                          </title>
                          <rect
                            x={point.x - 8}
                            y={230 - barHeight}
                            width="16"
                            height={barHeight}
                            rx="5"
                            fill="#31ce36"
                          />
                          <rect
                            x={point.x + 10}
                            y={230 - failHeight}
                            width="14"
                            height={failHeight || 0}
                            rx="5"
                            fill="#f25961"
                          />
                          <text
                            x={point.x}
                            y="266"
                            textAnchor="middle"
                            fontSize="10"
                            fill="#6c757d"
                            transform={`rotate(-45 ${point.x} 266)`}
                          >
                            {dayjs(point.item.date).format("DD/MM")}
                          </text>
                        </g>
                      );
                    })}

                    <path
                      d={linePath}
                      fill="none"
                      stroke="#1572e8"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {chartPoints.map((point) => (
                      <g key={`${point.item.date}-point`} style={{ cursor: "default" }}>
                        <title>
                          {`${dayjs(point.item.date).format(
                            "DD/MM/YYYY",
                          )} | Acessos Únicos: ${
                            point.item.uniqueAccesses
                          } | Sucesso: ${
                            point.item.successfulAccesses
                          } | Falhas: ${point.item.failedAccesses} | Total Geral: ${
                            point.item.totalAccesses
                          }`}
                        </title>
                        <circle cx={point.x} cy={point.y} r="5" fill="#1572e8" />
                        <text
                          x={point.x}
                          y={point.y - 10}
                          textAnchor="middle"
                          fontSize="11"
                          fontWeight="700"
                          fill="#1572e8"
                          stroke="#ffffff"
                          strokeWidth="4"
                          paintOrder="stroke"
                        >
                          {point.item.totalAccesses}
                        </text>
                      </g>
                    ))}
                  </svg>
                </div>
              </div>
            </Card>
          </div>

          <div className="col-12 col-xl-3">
            <Card className="h-100 shadow-sm border-0">
              <div className="card-header">
                <h4 className="card-title mb-1">Leitura Rápida</h4>
                <p className="card-category mb-0">
                  Destaques do período selecionado.
                </p>
              </div>
              <div className="card-body d-flex flex-column gap-3">
                <div className="border rounded p-3">
                  <div className="text-muted mb-1">Pico de Acessos</div>
                  <div className="fw-bold fs-4">
                    {bestDay ? bestDay.totalAccesses.toLocaleString("pt-BR") : "--"}
                  </div>
                  <small className="text-muted">
                    {bestDay ? dayjs(bestDay.date).format("DD/MM/YYYY") : "--"}
                  </small>
                </div>
                <div className="border rounded p-3">
                  <div className="text-muted mb-1">Taxa de Sucesso</div>
                  <div className="fw-bold fs-4">
                    {successRate.toLocaleString("pt-BR", {
                      maximumFractionDigits: 1,
                    })}
                    %
                  </div>
                  <div className="progress mt-2" style={{ height: 8 }}>
                    <div
                      className="progress-bar bg-success"
                      style={{ width: `${successRate}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div className="row">
          <div className="col-md-12">
            <Card>
              <div className="card-header">
                <div className="row">
                  <div className="col-md-7">
                    <h4 className="card-title">Histórico de Logins</h4>
                    <p className="card-category">
                      Registros de autenticação dos usuários no período.
                    </p>
                  </div>
                  <div className="col-md-5 d-flex flex-column flex-md-row justify-content-md-end align-items-md-center gap-2 mt-3 mt-md-0">
                    <div className="input-group input-group-sm" style={{ maxWidth: 210 }}>
                      <label className="input-group-text" htmlFor="take">
                        Exibir
                      </label>
                      <select
                        id="take"
                        className="form-select"
                        value={take}
                        onChange={(event) => {
                          setPage(1);
                          setTake(Number(event.target.value));
                        }}
                      >
                        {takeOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      <span className="input-group-text">por página</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Nome</th>
                        <th>Documento</th>
                        <th>Resultado</th>
                        <th>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={6} className="text-center">
                            <LoadingSkeleton />
                          </td>
                        </tr>
                      ) : (
                        <>
                          {data.logs.list.map((log) => (
                            <tr key={log.id}>
                              <td>{log.customerId}</td>
                              <td title={formatName(log.name)}>
                                {formatName(abbreviateName(log.name, 28))}
                              </td>
                              <td>{formatDocument(log.document)}</td>
                              <td>
                                <span
                                  className={`fs-6 badge badge-${
                                    log.success === "y" ? "success" : "danger"
                                  }`}
                                >
                                  {log.success === "y" ? "Sucesso" : "Falha"}
                                </span>
                              </td>
                              <td>
                                {dayjs(log.createdAt).format(
                                  "DD/MM/YYYY HH:mm",
                                )}
                              </td>
                            </tr>
                          ))}

                          {data.logs.list.length === 0 && (
                            <tr>
                              <td colSpan={6} className="text-center">
                                Nenhum registro encontrado.
                              </td>
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center">
                  <span>
                    Quantidade de Registros: {masks.formatMilhar(logAmount)}
                  </span>
                  <Pagination
                    page={page}
                    setPage={setPage}
                    totalPages={totalPages}
                    maxPageShow={10}
                  />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
